import {
  FormatBold,
  FormatItalic,
  FormatListBulleted,
  FormatListNumbered,
  FormatQuote,
  FormatStrikethrough,
  Image as ImageIcon,
  InsertLink,
  LinkOff,
  PictureAsPdf,
  Videocam,
} from '@mui/icons-material'
import { Box, Divider, IconButton, ToggleButton, Tooltip } from '@mui/material'
import { Editor } from '@tiptap/react'
import { FC, useRef } from 'react'
import { DOC_MEDIA_ACCEPT } from '../docs.constants'
import { DocMediaKind, UploadedDocMedia } from '../../../interfaceTypes/docs'

interface EditorToolbarProps {
  editor: Editor | null
  onUpload: (file: File) => Promise<UploadedDocMedia | null>
  uploading: boolean
}

const HEADINGS: { level: 1 | 2 | 3; label: string }[] = [
  { level: 1, label: 'H1' },
  { level: 2, label: 'H2' },
  { level: 3, label: 'H3' },
]

export const EditorToolbar: FC<EditorToolbarProps> = ({ editor, onUpload, uploading }) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const pendingKind = useRef<DocMediaKind>('image')

  if (!editor) return null

  const triggerUpload = (kind: DocMediaKind) => {
    pendingKind.current = kind
    if (fileInputRef.current) {
      fileInputRef.current.accept = DOC_MEDIA_ACCEPT[kind]
      fileInputRef.current.value = ''
      fileInputRef.current.click()
    }
  }

  const handleFileSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return
    const asset = await onUpload(file)
    if (!asset) return
    const kind = pendingKind.current
    if (kind === 'image') {
      editor.chain().focus().setImage({ src: asset.url, s3Key: asset.key } as any).run()
    } else if (kind === 'video') {
      editor.chain().focus().setVideo({ src: asset.url, s3Key: asset.key, title: asset.fileName }).run()
    } else {
      editor.chain().focus().setPdf({ src: asset.url, s3Key: asset.key, fileName: asset.fileName }).run()
    }
  }

  const setLink = () => {
    const previous = editor.getAttributes('link').href
    const url = window.prompt('Enter URL', previous || 'https://')
    if (url === null) return
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run()
      return
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 0.5,
        p: 1,
        borderBottom: '1px solid',
        borderColor: 'divider',
        position: 'sticky',
        top: 0,
        bgcolor: 'background.paper',
        zIndex: 1,
      }}
    >
      <Tooltip title="Bold">
        <ToggleButton value="bold" size="small" selected={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()}>
          <FormatBold fontSize="small" />
        </ToggleButton>
      </Tooltip>
      <Tooltip title="Italic">
        <ToggleButton value="italic" size="small" selected={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()}>
          <FormatItalic fontSize="small" />
        </ToggleButton>
      </Tooltip>
      <Tooltip title="Strikethrough">
        <ToggleButton value="strike" size="small" selected={editor.isActive('strike')} onClick={() => editor.chain().focus().toggleStrike().run()}>
          <FormatStrikethrough fontSize="small" />
        </ToggleButton>
      </Tooltip>

      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

      {HEADINGS.map(({ level, label }) => (
        <ToggleButton
          key={level}
          value={`h${level}`}
          size="small"
          selected={editor.isActive('heading', { level })}
          onClick={() => editor.chain().focus().toggleHeading({ level }).run()}
          sx={{ fontWeight: 700, px: 1 }}
        >
          {label}
        </ToggleButton>
      ))}

      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

      <Tooltip title="Bullet list">
        <ToggleButton value="bullet" size="small" selected={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()}>
          <FormatListBulleted fontSize="small" />
        </ToggleButton>
      </Tooltip>
      <Tooltip title="Numbered list">
        <ToggleButton value="ordered" size="small" selected={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()}>
          <FormatListNumbered fontSize="small" />
        </ToggleButton>
      </Tooltip>
      <Tooltip title="Quote">
        <ToggleButton value="quote" size="small" selected={editor.isActive('blockquote')} onClick={() => editor.chain().focus().toggleBlockquote().run()}>
          <FormatQuote fontSize="small" />
        </ToggleButton>
      </Tooltip>

      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

      <Tooltip title="Text color">
        <Box
          component="input"
          type="color"
          aria-label="Text color"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => editor.chain().focus().setColor(e.target.value).run()}
          sx={{ width: 30, height: 30, p: 0, border: '1px solid', borderColor: 'divider', borderRadius: 1, cursor: 'pointer', bgcolor: 'transparent' }}
        />
      </Tooltip>

      <Tooltip title={editor.isActive('link') ? 'Edit link' : 'Add link'}>
        <ToggleButton value="link" size="small" selected={editor.isActive('link')} onClick={setLink}>
          <InsertLink fontSize="small" />
        </ToggleButton>
      </Tooltip>
      {editor.isActive('link') && (
        <Tooltip title="Remove link">
          <IconButton size="small" onClick={() => editor.chain().focus().unsetLink().run()}>
            <LinkOff fontSize="small" />
          </IconButton>
        </Tooltip>
      )}

      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

      <Tooltip title="Insert image">
        <span>
          <IconButton size="small" disabled={uploading} onClick={() => triggerUpload('image')}>
            <ImageIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Insert video">
        <span>
          <IconButton size="small" disabled={uploading} onClick={() => triggerUpload('video')}>
            <Videocam fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title="Insert PDF">
        <span>
          <IconButton size="small" disabled={uploading} onClick={() => triggerUpload('pdf')}>
            <PictureAsPdf fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>

      <input ref={fileInputRef} type="file" hidden onChange={handleFileSelected} />
    </Box>
  )
}
